// src/pages/spot/hooks/useLandmarkManager.js
// 랜드마크 상태 관리 및 액션 처리를 위한 커스텀 훅
// 🎯 [Cost] 전체 컬렉션 onSnapshot 제거 → 화면 내 ID만 getDocs + "내 땅" 쿼리 + TTL 캐시

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db } from '../../../firebase';
import { collection, query, where, getDocs, documentId, limit } from 'firebase/firestore';
import { spotGoAction } from '../../../services/api';
import { getEnvironment, getCollectionPath } from '../../../utils/environment';

const VISIBLE_CACHE_TTL_MS = 5 * 60 * 1000;  // 5분
const MY_LANDS_CACHE_TTL_MS = 5 * 60 * 1000; // 5분
const MY_LAND_STATUS_CACHE_TTL_MS = 5 * 60 * 1000; // 5분
const IN_QUERY_LIMIT = 30; // Firestore 'in' 쿼리 최대 30개

const cacheStore = { visible: {}, myLands: {}, myLandStatus: {} };

const statusMapSignature = (statusMap) => JSON.stringify(statusMap, (_key, value) => {
    if (value && typeof value.toMillis === 'function') return value.toMillis();
    return value;
});

/**
 * 랜드마크 방문 시스템을 위한 커스텀 훅
 * @param {Object} piUser - Pi 사용자 정보
 * @param {Function} showAlert - Alert 표시 함수
 * @param {Function} t - i18n 번역 함수
 * @param {Object} options - { spots: Array } 반경 내 스팟 목록 (화면 내 랜드마크 ID 추출용)
 */
const useLandmarkManager = (piUser, showAlert, t, options = {}) => {
    const { spots = [], myLandCapture } = options;
    const [landmarkStatus, setLandmarkStatus] = useState({});
    const [optimisticLandmarks, setOptimisticLandmarks] = useState({});
    const [loading, setLoading] = useState({});
    const loadingRef = useRef({});
    const myLandsCacheInvalidatedRef = useRef(false);
    const myLandStatusInvalidatedRef = useRef(false);

    const env = getEnvironment();
    const collectionName = getCollectionPath('landmark_status', env);
    const myLandStatusCol = getCollectionPath('my_land_status', env);

    const landmarkIdsInView = useMemo(
        () => Array.from(new Set(
            (spots || []).filter(s => s.type === 'landmark' && s.id).map(s => s.id)
        )).sort(),
        [spots]
    );

    // 🎯 [Cost] 화면 내 ID + "내 땅" 만 getDocs (onSnapshot 제거)
    useEffect(() => {
        let cancelled = false;
        const colRef = collection(db, collectionName);

        const fetchVisible = async (ids) => {
            if (ids.length === 0) return {};
            const sorted = [...ids].sort();
            const cacheKey = `visible-${sorted.join(',')}-${env}`;
            const cached = cacheStore.visible[cacheKey];
            if (cached && (Date.now() - cached.ts) < VISIBLE_CACHE_TTL_MS) {
                return cached.data;
            }
            const statuses = {};
            for (let i = 0; i < ids.length; i += IN_QUERY_LIMIT) {
                const chunk = ids.slice(i, i + IN_QUERY_LIMIT);
                const q = query(colRef, where(documentId(), 'in', chunk));
                const snapshot = await getDocs(q);
                snapshot.forEach((doc) => {
                    statuses[doc.id] = { id: doc.id, ...doc.data() };
                });
            }
            cacheStore.visible[cacheKey] = { data: statuses, ts: Date.now() };
            return statuses;
        };

        const fetchMyLands = async () => {
            if (!piUser?.uid) return {};
            const cacheKey = `myLands-${piUser.uid}-${env}`;
            if (!myLandsCacheInvalidatedRef.current) {
                const cached = cacheStore.myLands[cacheKey];
                if (cached && (Date.now() - cached.ts) < MY_LANDS_CACHE_TTL_MS) {
                    return cached.data;
                }
            }
            myLandsCacheInvalidatedRef.current = false;
            const q = query(colRef, where('ownerId', '==', piUser.uid), limit(3));
            const snapshot = await getDocs(q);
            const statuses = {};
            snapshot.forEach((doc) => {
                statuses[doc.id] = { id: doc.id, ...doc.data() };
            });
            cacheStore.myLands[cacheKey] = { data: statuses, ts: Date.now() };
            return statuses;
        };

        const fetchMyLandStatus = async () => {
            if (!piUser?.uid) return {};
            const cacheKey = `myLandStatus-${piUser.uid}-${env}`;
            if (!myLandStatusInvalidatedRef.current) {
                const cached = cacheStore.myLandStatus[cacheKey];
                if (cached && (Date.now() - cached.ts) < MY_LAND_STATUS_CACHE_TTL_MS) {
                    return cached.data;
                }
            }
            myLandStatusInvalidatedRef.current = false;
            const myLandColRef = collection(db, myLandStatusCol);
            const q = query(myLandColRef, where('ownerId', '==', piUser.uid), limit(3));
            const snapshot = await getDocs(q);
            const statuses = {};
            snapshot.forEach((doc) => {
                statuses[doc.id] = { id: doc.id, ...doc.data() };
            });
            cacheStore.myLandStatus[cacheKey] = { data: statuses, ts: Date.now() };
            return statuses;
        };

        const run = async () => {
            try {
                const [visibleMap, myLandsMap, myLandStatusMap] = await Promise.all([
                    fetchVisible(landmarkIdsInView),
                    piUser?.uid ? fetchMyLands() : Promise.resolve({}),
                    piUser?.uid ? fetchMyLandStatus() : Promise.resolve({})
                ]);
                if (cancelled) return;
                const nextStatus = { ...visibleMap, ...myLandsMap, ...myLandStatusMap };
                setLandmarkStatus((prev) => (
                    statusMapSignature(prev) === statusMapSignature(nextStatus) ? prev : nextStatus
                ));
            } catch (err) {
                if (!cancelled) console.error('[useLandmarkManager] fetch error:', err);
            }
        };

        run();
        return () => { cancelled = true; };
    }, [collectionName, myLandStatusCol, env, landmarkIdsInView, piUser?.uid]);

    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);

    // 방문 핸들러 (Optimistic UI)
    const handleVisit = useCallback(async (landmarkId, landmarkData = {}, options = {}) => {
        const suppressAlert = !!options?.suppressAlert;
        const maybeAlert = (msg) => {
            if (!suppressAlert) showAlert(msg);
        };
        if (!piUser?.uid) {
            maybeAlert(t('login_required'));
            return { success: false, error: 'LOGIN_REQUIRED' };
        }
        if (loadingRef.current[landmarkId]) return { success: false, error: 'IN_PROGRESS' };

        setLoading(prev => ({ ...prev, [landmarkId]: true }));

        setOptimisticLandmarks(prev => ({
            ...prev,
            [landmarkId]: {
                ownerId: piUser.uid,
                ownerName: piUser.username || 'Me',
                pending: true
            }
        }));

        try {
            const isAdReward = options?.isAdReward || false;
            const isMyLandSlot = typeof landmarkId === 'string' && landmarkId.startsWith('myland_');
            if (isMyLandSlot && myLandCapture) {
                const parts = landmarkId.split('_');
                const slot = parseInt(parts[parts.length - 1], 10);
                if ([1, 2, 3].includes(slot)) {
                    const result = await myLandCapture(piUser.uid, env, slot, piUser, false);
                    if (!result?.success) {
                        const err = result?.error || '';
                        if (err === 'INSUFFICIENT_TICKETS') maybeAlert(t('my_land_insufficient_tickets'));
                        else if (err === 'LAND_LIMIT') maybeAlert(t('my_land_limit_reached'));
                        else if (err === 'NOT_REGISTERED') maybeAlert(t('my_land_not_registered'));
                        else if (err === 'SLOT_NOT_PURCHASED') maybeAlert(t('my_land_slot_not_purchased'));
                        else if (err === 'SLOT_EXPIRED') maybeAlert(t('my_land_slot_expired'));
                        else maybeAlert(t('landmark_visit_failed'));
                        return { success: false, error: err || 'CAPTURE_FAILED' };
                    }
                    if (result.alreadyOwned) {
                        maybeAlert(t('my_land_already_own'));
                    } else {
                        maybeAlert(t('my_land_capture_success'));
                    }
                    myLandStatusInvalidatedRef.current = true;
                    // DB 추가 조회 없이 로컬 상태/캐시 즉시 반영 (API/DB 호출 최소화)
                    const nowMs = Date.now();
                    const fallbackCapturedAt = result?.capturedAt ? { toDate: () => new Date(result.capturedAt), toMillis: () => result.capturedAt } : { toDate: () => new Date(nowMs), toMillis: () => nowMs };
                    const fallbackExpiresAt = result?.expiresAt ? { toDate: () => new Date(result.expiresAt), toMillis: () => result.expiresAt } : undefined;
                    const fallbackProtectedUntil = result?.protectedUntil ? { toDate: () => new Date(result.protectedUntil), toMillis: () => result.protectedUntil } : undefined;
                    const myLandStatusMap = {
                        [landmarkId]: {
                            id: landmarkId,
                            landmarkId,
                            ownerId: piUser.uid,
                            ownerName: piUser.username || 'Me',
                            landmarkName: landmarkData?.name || t('my_land_title'),
                            lat: landmarkData?.geo?.lat,
                            lng: landmarkData?.geo?.lng,
                            capturedAt: fallbackCapturedAt,
                            protectedUntil: fallbackProtectedUntil,
                            expiresAt: fallbackExpiresAt
                        }
                    };
                    const cacheKey = `myLandStatus-${piUser.uid}-${env}`;
                    const prevData = cacheStore.myLandStatus[cacheKey]?.data || {};
                    cacheStore.myLandStatus[cacheKey] = { data: { ...prevData, ...myLandStatusMap }, ts: Date.now() };
                    setLandmarkStatus(prev => ({ ...prev, ...myLandStatusMap }));
                    return { success: true, alreadyOwned: !!result.alreadyOwned };
                } else {
                    maybeAlert(t('landmark_visit_failed'));
                    return { success: false, error: 'INVALID_SLOT' };
                }
            } else if (isMyLandSlot && !myLandCapture) {
                maybeAlert(t('landmark_visit_failed'));
                return { success: false, error: 'CAPTURE_HANDLER_MISSING' };
            } else {
                await spotGoAction('capture', landmarkId, piUser, getEnvironment(), landmarkData, isAdReward);

                maybeAlert(t('landmark_visit_success'));
                myLandsCacheInvalidatedRef.current = true;
                const colRef = collection(db, collectionName);
                const q = query(colRef, where('ownerId', '==', piUser.uid), limit(3));
                const snapshot = await getDocs(q);
                const myLandsMap = {};
                snapshot.forEach((doc) => {
                    myLandsMap[doc.id] = { id: doc.id, ...doc.data() };
                });
                const cacheKey = `myLands-${piUser.uid}-${env}`;
                cacheStore.myLands[cacheKey] = { data: myLandsMap, ts: Date.now() };
                setLandmarkStatus(prev => ({ ...prev, ...myLandsMap }));
                return { success: true };
            }
        } catch (error) {
            setOptimisticLandmarks(prev => {
                const updated = { ...prev };
                delete updated[landmarkId];
                return updated;
            });

            const isSettled = error.settled || (error.details && error.details.settled);
            if (isSettled) {
                maybeAlert(t('landmark_settled_only'));
                return { success: false, error: 'SETTLED' };
            }

            let errorMsg = error.code || error.error || error.message;
            if (errorMsg && errorMsg.includes('LAND_LIMIT')) errorMsg = 'LAND_LIMIT';
            else if (errorMsg && errorMsg.includes('PROTECTED')) errorMsg = 'PROTECTED';
            else if (errorMsg && errorMsg.includes('INSUFFICIENT_TICKETS')) errorMsg = 'INSUFFICIENT_TICKETS';

            if (errorMsg === 'INSUFFICIENT_TICKETS') {
                const required = error.details?.required || 1;
                maybeAlert(t('landmark_insufficient_tickets', { count: required }));
            } else if (errorMsg === 'LAND_LIMIT') {
                const currentCount = error.details?.currentCount ?? error.currentCount ?? '?';
                const maxCount = error.details?.maxCount ?? error.maxCount ?? 3;
                if (error.settled || (error.details && error.details.settled)) {
                    maybeAlert(t('landmark_settled_limit_reached', { current: currentCount, max: maxCount }));
                } else {
                    maybeAlert(t('landmark_limit_reached', { current: currentCount, max: maxCount }));
                }
            } else if (errorMsg === 'PROTECTED') {
                const minutes = error.details?.remainingMinutes || '?';
                maybeAlert(t('landmark_protected', { minutes }));
            } else if (errorMsg === 'USER_NOT_FOUND') {
                maybeAlert(t('landmark_user_not_found'));
            } else if (errorMsg === 'MISSING_FIELDS') {
                maybeAlert(t('landmark_missing_fields'));
            } else if (errorMsg === 'INVALID_ACTION') {
                maybeAlert(t('landmark_invalid_action'));
            } else if (error.message && (
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError') ||
                error.message.includes('ERR_INTERNET_DISCONNECTED')
            )) {
                maybeAlert(t('landmark_network_error'));
            } else {
                maybeAlert(t('landmark_visit_failed'));
            }
            return { success: false, error: errorMsg || 'VISIT_FAILED' };
        } finally {
            setLoading(prev => ({ ...prev, [landmarkId]: false }));
            setTimeout(() => {
                setOptimisticLandmarks(prev => {
                    const updated = { ...prev };
                    delete updated[landmarkId];
                    return updated;
                });
            }, 2000);
        }
    }, [piUser, showAlert, t, collectionName, env, myLandCapture]);

    const getLandmarkState = useCallback((landmarkId) => {
        const optimistic = optimisticLandmarks[landmarkId];
        const firestore = landmarkStatus[landmarkId];
        const actualStatus = firestore;
        const displayStatus = optimistic || firestore;

        const now = Date.now();
        const expiresAt = actualStatus?.expiresAt?.toDate ? actualStatus.expiresAt.toDate().getTime() : (actualStatus?.expiresAt || 0);
        const isExpired = expiresAt > 0 && expiresAt < now;
        const protectedUntil = actualStatus?.protectedUntil?.toDate ? actualStatus.protectedUntil.toDate().getTime() : (actualStatus?.protectedUntil || 0);
        const isProtected = protectedUntil > 0 && protectedUntil > now;
        const isEmpty = !actualStatus || isExpired;
        const isMyLand = actualStatus?.ownerId === piUser?.uid && !isExpired;
        const isPending = optimistic?.pending;
        const isLoading = loading[landmarkId];

        return {
            status: displayStatus,
            actualStatus,
            isMyLand,
            isEmpty,
            isPending,
            isLoading,
            isProtected
        };
    }, [landmarkStatus, optimisticLandmarks, piUser?.uid, loading]);

    return {
        landmarkStatus,
        handleVisit,
        getLandmarkState
    };
};

/**
 * ✅ v4.1: 예상 적립 SPOT 계산 헬퍼 함수 (자동 정산용)
 */
export const calculateEstimatedEarnings = (landmarkState, spotRate, limitHours = 24, now = new Date(), bonusMultiplier = 1) => {
    if (!spotRate) {
        console.error('[calculateEstimatedEarnings] spotRate is null/undefined - DB not loaded');
        return { estimatedSpot: 0, elapsedMinutes: 0, cappedMinutes: 0 };
    }
    if (!landmarkState || !landmarkState.capturedAt) {
        return { estimatedSpot: 0, elapsedMinutes: 0, cappedMinutes: 0 };
    }

    const capturedAt = landmarkState.capturedAt?.toDate?.() || landmarkState.capturedAt;
    const capturedTimestamp = new Date(capturedAt).getTime();
    const nowTimestamp = now.getTime();
    const elapsedMs = nowTimestamp - capturedTimestamp;
    const elapsedMinutes = Math.floor(elapsedMs / (60 * 1000));
    const cappedMinutes = Math.min(elapsedMinutes, limitHours * 60);
    const estimatedSpot = cappedMinutes * spotRate * bonusMultiplier;

    return {
        estimatedSpot: parseFloat(estimatedSpot.toFixed(1)),
        elapsedMinutes,
        cappedMinutes,
        bonusApplied: bonusMultiplier > 1
    };
};

export default useLandmarkManager;
